import {Injectable, Optional} from '@angular/core';

import {DateFormatService, DATE_FORMAT_YMD} from '@ngmedax/translation';


declare const $: any;

/**
 * Locale to date format mapping for used locales.
 */
const LOCALE_DATE_FORMATS: {[locale: string]: string} = {
  'de_DE': 'DD.MM.YYYY',
  'en_US': 'MM/DD/YYYY'
};

/**
 * Default date format as fallback.
 */
const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';

/**
 * Service for handling patient date formatting and jQuery mask integration.
 * Provides date conversion utilities and abstracts jQuery dependencies.
 */
@Injectable()
export class PatientDateService {
  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private dateFormatService: DateFormatService
  ) {
  }

  /**
   * Returns the YMD date format for the given locale.
   * E.g: "MM/DD/YYYY" for en_US, "DD.MM.YYYY" for de_DE.
   *
   * @param locale The locale string.
   * @returns The date format string.
   */
  public getLocalDateFormat(locale: string): string {
    return LOCALE_DATE_FORMATS[locale] || DEFAULT_DATE_FORMAT;
  }

  /**
   * Returns the date format mask for the given locale.
   * E.g: "00/00/0000" for en_US, "00.00.0000" for de_DE.
   *
   * @param locale The locale string.
   * @returns The date format mask string.
   */
  public getLocalDateFormatMask(locale: string): string {
    return this.getLocalDateFormat(locale).replace(/[YMD]/g, '0');
  }

  /**
   * Converts a local YMD date to MySQL date format (YYYY-MM-DD).
   *
   * @param date The date string in local format.
   * @param locale The locale string.
   * @returns The date in MySQL format.
   */
  public toMySqlDate(date: string, locale: string): string {
    const format = this.getLocalDateFormat(locale);
    return this.dateFormatService ? this.dateFormatService.toMySqlDate(date, format) : date;
  }

  /**
   * Converts a MySQL date (YYYY-MM-DD) to local YMD format.
   *
   * @param date The date string in MySQL format.
   * @param locale The locale string.
   * @returns The date in local format.
   */
  public fromMySqlDate(date: string, locale: string): string {
    const format = this.getLocalDateFormat(locale);
    return this.dateFormatService ? this.dateFormatService.fromMySqlDate(date, format) : date;
  }

  /**
   * Converts a date from one locale to another.
   *
   * @param date The date string.
   * @param fromLocale The source locale.
   * @param toLocale The target locale.
   * @returns The converted date string.
   */
  public convertLocale(date: string, fromLocale: string, toLocale: string): string {
    if (!this.dateFormatService) {
      return date;
    }

    return this.dateFormatService.convert({
      fromLocale,
      toLocale,
      dateFormat: DATE_FORMAT_YMD,
      date
    });
  }

  /**
   * Checks if a date string is in MySQL format (YYYY-MM-DD).
   *
   * @param date The date string to check.
   * @returns true if the date is in MySQL format.
   */
  public isMySqlFormat(date: string): boolean {
    return !!date && !!date.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
  }

  /**
   * Checks if a date string is in German format (DD.MM.YYYY).
   *
   * @param date The date string to check.
   * @returns true if the date is in German format.
   */
  public isGermanFormat(date: string): boolean {
    return !!date && !!date.match(/^([0-9]{2})\.([0-9]{2})\.([0-9]{4})$/);
  }

  /**
   * Renders the date mask on a date input field using jQuery mask plugin.
   *
   * @param elementId The element ID (without #).
   * @param locale The locale for the date format.
   */
  public renderDateMask(elementId: string, locale: string): void {
    if (typeof $ !== 'function' || !$('').mask) {
      return;
    }

    const mask = this.getLocalDateFormatMask(locale);
    const placeholder = this.getLocalDateFormat(locale);
    $(`#${elementId}`).mask(mask, {placeholder});
  }
}
